import type { ApplicationRef } from '@angular/core';

import type { ConsentService } from '../consent/consent.service';
import { AuthService } from '../services/auth.service';

const BUNDLE_SCRIPT_MARKER = 'data-pickapeak-feedback-widget';

function destroyFeedbackWidget(): void {
  document.querySelectorAll('feedback-widget-app').forEach((n) => n.remove());
}

async function loadFeedbackWidgetBundle(): Promise<void> {
  if (window.FeedbackWidget) {
    return;
  }

  const existing = document.querySelector(`script[${BUNDLE_SCRIPT_MARKER}]`);
  if (existing) {
    await new Promise<void>((resolve, reject) => {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('feedback-widget script failed')), {
        once: true,
      });
    });
    return;
  }

  const version = (await fetch('/feedback-widget.version.json', { cache: 'no-store' }).then((r) =>
    r.json(),
  )) as { hash: string };

  await new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `/feedback-widget.js?v=${version.hash}`;
    script.defer = true;
    script.setAttribute(BUNDLE_SCRIPT_MARKER, '1');
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('feedback-widget script failed'));
    document.head.appendChild(script);
  });

  if (!window.FeedbackWidget) {
    throw new Error('FeedbackWidget global missing after script load');
  }
}

/**
 * Pick a Peak page feedback widget: mount only when feedback consent is granted.
 * Loaded from /public/feedback-widget.js (not Vite prebundle) so rebuilds are picked up.
 */
export function initializeFeedbackWidgetWithConsent(
  applicationRef: ApplicationRef,
  consent: ConsentService,
  apiBaseUrl: string,
): void {
  const auth = applicationRef.injector.get(AuthService);
  const base = (apiBaseUrl ?? '').replace(/\/$/, '');

  const run = async (): Promise<void> => {
    if (!base || !consent.feedbackWidgetAllowed()) {
      destroyFeedbackWidget();
      return;
    }

    try {
      await loadFeedbackWidgetBundle();
      window.FeedbackWidget!.init({
        apiBaseUrl: base,
        getAccessToken: () => auth.getAccessToken(),
      });
    } catch (err) {
      console.warn('[feedback-widget] failed to load', err);
    }
  };

  void run();
  consent.changed$.subscribe(() => void run());
}
